/**
 * Sorting concern for DataViewTable.
 *
 * Owns the sort state (`orderBy` / `order` / `hasUserSorted`), the
 * first-render compound sort and the sorted-rows memo. Extracted as a
 * pure structural move - the state, memo dependency arrays and the
 * compound-vs-single-column branch are byte-identical to the inline
 * originals.
 */
import { useMemo, useState } from 'react'
import type {
  ExceptionColumnDef,
  ExceptionConfig,
  ExceptionContext,
} from '../../../config/exceptions/types'

/**
 * Get sort value for a row, using custom extractor or raw value
 */
const getSortValue = (row: Record<string, any>, col: ExceptionColumnDef): any => {
  if (col.getSortValue) return col.getSortValue(row)
  return row[col.key]
}

/**
 * Generic comparator for sorting
 */
const compare = (a: any, b: any, order: 'asc' | 'desc'): number => {
  if (a == null && b == null) return 0
  if (a == null) return order === 'asc' ? 1 : -1
  if (b == null) return order === 'asc' ? -1 : 1

  if (typeof a === 'number' && typeof b === 'number') {
    return order === 'asc' ? a - b : b - a
  }

  const cmp = String(a).localeCompare(String(b))
  return order === 'asc' ? cmp : -cmp
}

/**
 * Opt into server-owned sorting. The parent owns the sort state (URL
 * params + refetch); header clicks on keys in `sortableKeys` call
 * `onSortChange` instead of mutating local state, the data passes
 * through unsorted, and columns outside the set render without a sort
 * affordance. Omitted = today's client-side behavior, unchanged.
 */
export interface ServerSortConfig {
  orderBy: string
  order: 'asc' | 'desc'
  sortableKeys: string[]
  onSortChange: (key: string) => void
}

export function useTableSorting({
  filteredData,
  visibleColumns,
  config,
  variant,
  serverSort,
}: {
  filteredData: Record<string, any>[]
  visibleColumns: ExceptionColumnDef[]
  config: ExceptionConfig
  variant: ExceptionContext
  serverSort?: ServerSortConfig
}) {
  const [orderBy, setOrderBy] = useState(config.defaultSortColumn)
  const [order, setOrder] = useState<'asc' | 'desc'>(config.defaultSortDirection)
  // Tracks whether the user has clicked a sortable column header. While
  // false, sorting uses the compound default (fp_name + secondary).
  const [hasUserSorted, setHasUserSorted] = useState(false)

  // First-render compound sort (platform variant): until the user picks
  // a column header, sort by ``fp_name`` first then by the config's
  // ``defaultSortColumn``. Inspector tables stay single-key by default
  // - their fpCode is fixed so a franchise-name pre-sort is a no-op.
  // Configs can pin their own compound order via ``defaultSortKeys``.
  const compoundSortKeys = useMemo<string[]>(() => {
    if (config.defaultSortKeys && config.defaultSortKeys.length > 0) {
      return config.defaultSortKeys
    }
    if (variant !== 'platform') return [config.defaultSortColumn]
    const primary = 'fp_name'
    const secondary = config.defaultSortColumn && config.defaultSortColumn !== primary
      ? [config.defaultSortColumn]
      : []
    return [primary, ...secondary]
  }, [config.defaultSortKeys, config.defaultSortColumn, variant])

  const sortedData = useMemo(() => {
    // Server mode: the rows arrived in server order - pass through.
    if (serverSort) return filteredData
    if (!hasUserSorted && compoundSortKeys.length > 1) {
      const sortCols = compoundSortKeys
        .map(k => visibleColumns.find(c => c.key === k))
        .filter((c): c is ExceptionColumnDef => !!c)
      if (sortCols.length === 0) return filteredData
      return [...filteredData].sort((a, b) => {
        for (const c of sortCols) {
          const cmp = compare(getSortValue(a, c), getSortValue(b, c), order)
          if (cmp !== 0) return cmp
        }
        return 0
      })
    }
    const col = visibleColumns.find(c => c.key === orderBy)
    if (!col) return filteredData
    return [...filteredData].sort((a, b) => {
      const aVal = getSortValue(a, col)
      const bVal = getSortValue(b, col)
      return compare(aVal, bVal, order)
    })
  }, [filteredData, orderBy, order, visibleColumns, hasUserSorted, compoundSortKeys, serverSort])

  const handleSort = (key: string) => {
    if (serverSort) {
      // Server mode: delegate keys the server can sort; ignore the rest
      // (their headers render without a sort affordance anyway).
      if (serverSort.sortableKeys.includes(key)) serverSort.onSortChange(key)
      return
    }
    if (orderBy === key) {
      setOrder(order === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(key)
      setOrder('desc')
    }
    // User picked a column - exit the compound first-render sort.
    setHasUserSorted(true)
  }

  return {
    sortedData,
    // Displayed sort state mirrors the server's when it owns sorting.
    orderBy: serverSort ? serverSort.orderBy : orderBy,
    order: serverSort ? serverSort.order : order,
    handleSort,
  }
}
